import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit2, Trash2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const NC = () => <span className="text-amber-600 text-xs font-normal" title="Not stated in lender guidelines">Not Confirmed</span>;

const ProductMatrix = ({ lenderId, programs = [], onUpdate }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [formData, setFormData] = useState({
    program_name: '',
    loan_type: '',
    min_fico: '',
    max_ltv: '',
    min_loan_amount: '',
    max_loan_amount: '',
    interest_rate: '',
    term_months: '',
    property_types: '',
    notes: ''
  });

  const resetForm = () => {
    setFormData({
      program_name: '',
      loan_type: '',
      min_fico: '',
      max_ltv: '',
      min_loan_amount: '',
      max_loan_amount: '',
      interest_rate: '',
      term_months: '',
      property_types: '',
      notes: ''
    });
    setEditingProgram(null);
  };

  const handleEdit = (program) => {
    setEditingProgram(program);
    setFormData({
      program_name: program.program_name,
      loan_type: program.loan_type,
      min_fico: program.min_fico == null ? '' : String(program.min_fico),
      max_ltv: program.max_ltv == null ? '' : String(program.max_ltv),
      min_loan_amount: program.min_loan_amount == null ? '' : String(program.min_loan_amount),
      max_loan_amount: program.max_loan_amount == null ? '' : String(program.max_loan_amount),
      interest_rate: program.interest_rate || '',
      term_months: program.term_months == null ? '' : String(program.term_months),
      property_types: (program.property_types || []).join(', '),
      notes: program.notes || ''
    });
    setShowDialog(true);
  };

  const handleSubmit = () => {
    const num = (v) => (v === '' || v == null || Number.isNaN(Number(v)) ? null : Number(v));
    const newProgram = {
      ...(editingProgram || {}),
      program_id: editingProgram?.program_id || `prog_${Date.now()}`,
      program_name: formData.program_name,
      loan_type: formData.loan_type || null,
      min_fico: num(formData.min_fico),
      max_ltv: num(formData.max_ltv),
      min_loan_amount: num(formData.min_loan_amount),
      max_loan_amount: num(formData.max_loan_amount),
      interest_rate: formData.interest_rate || null,
      term_months: num(formData.term_months),
      property_types: formData.property_types.split(',').map(t => t.trim()).filter(Boolean),
      notes: formData.notes || null,
      status: editingProgram?.status || 'approved'
    };

    let updatedPrograms;
    if (editingProgram) {
      updatedPrograms = programs.map(p => 
        p.program_id === editingProgram.program_id ? newProgram : p
      );
    } else {
      updatedPrograms = [...programs, newProgram];
    }

    onUpdate(updatedPrograms);
    setShowDialog(false);
    resetForm();
    toast.success(editingProgram ? 'Program updated!' : 'Program added!');
  };

  const handleDelete = (programId) => {
    if (window.confirm('Are you sure you want to delete this program?')) {
      const updatedPrograms = programs.filter(p => p.program_id !== programId);
      onUpdate(updatedPrograms);
      toast.success('Program deleted');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Product Matrix</h3>
        <Button size="sm" onClick={() => { resetForm(); setShowDialog(true); }}>
          <Plus className="h-4 w-4 mr-2" />
          Add Program
        </Button>
      </div>

      {programs.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No loan programs added yet. Add programs to enable automatic lead matching.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {programs.map((program, index) => (
            <Card key={program.program_id} data-testid={`program-${index}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-base">{program.program_name}</CardTitle>
                    <Badge variant="secondary" className="mt-1">{program.loan_type || 'Loan type not confirmed'}</Badge>
                    {program.source_doc_id && <Badge variant="outline" className="mt-1 ml-1 text-xs">From PDF</Badge>}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(program)}>
                      <Edit2 className="h-3 w-3" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(program.program_id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <Label className="text-muted-foreground">Rate</Label>
                    <p className="font-medium text-primary">{program.interest_rate || <NC />}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Term</Label>
                    <p className="font-medium">{program.term_months != null ? `${program.term_months} months` : <NC />}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Min FICO</Label>
                    <p className="font-medium">{program.min_fico != null ? `${program.min_fico}+` : <NC />}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Max LTV</Label>
                    <p className="font-medium">{program.max_ltv != null ? `${program.max_ltv}%` : <NC />}</p>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-muted-foreground">Loan Range</Label>
                    <p className="font-medium">
                      {program.min_loan_amount == null && program.max_loan_amount == null ? <NC /> :
                        `${program.min_loan_amount != null ? `$${(program.min_loan_amount / 1000).toFixed(0)}K` : 'No min'} - ${program.max_loan_amount != null ? `$${(program.max_loan_amount / 1000).toFixed(0)}K` : 'No max'}`}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-muted-foreground">Property Types</Label>
                    <p className="text-xs">{(program.property_types || []).length ? program.property_types.join(', ') : <NC />}</p>
                  </div>
                </div>
                {program.notes && (
                  <p className="text-xs text-muted-foreground mt-3 border-t pt-3">
                    {program.notes}
                  </p>
                )}
                {(() => {
                  const extra = [['min_dscr', 'Min DSCR'], ['max_ltc', 'Max LTC'], ['points', 'Points'], ['fees', 'Fees'], ['amortization', 'Amortization'], ['prepayment_penalty', 'Prepay'], ['recourse', 'Recourse'], ['states', 'States']];
                  const missing = extra.filter(([k]) => program[k] == null || program[k] === '' || (Array.isArray(program[k]) && !program[k].length)).map(([, l]) => l);
                  const known = extra.filter(([k]) => !missing.includes(extra.find(([kk]) => kk === k)[1]));
                  return (
                    <div className="text-xs mt-3 border-t pt-3 space-y-1" data-testid={`program-${index}-extra`}>
                      {known.length > 0 && <p className="text-muted-foreground">{known.map(([k, l]) => `${l}: ${Array.isArray(program[k]) ? program[k].join(', ') : program[k]}`).join(' · ')}</p>}
                      {missing.length > 0 && <p className="text-amber-600"><span className="font-medium">Not Confirmed:</span> {missing.join(', ')}</p>}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProgram ? 'Edit Program' : 'Add Loan Program'}</DialogTitle>
            <DialogDescription>Define the criteria and terms for this loan program</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="program_name">Program Name *</Label>
                <Input
                  id="program_name"
                  value={formData.program_name}
                  onChange={(e) => setFormData({ ...formData, program_name: e.target.value })}
                  placeholder="e.g., Fix & Flip Premium"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="loan_type">Loan Type *</Label>
                <Select value={formData.loan_type} onValueChange={(value) => setFormData({ ...formData, loan_type: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Fix & Flip">Fix & Flip</SelectItem>
                    <SelectItem value="DSCR">DSCR</SelectItem>
                    <SelectItem value="Bridge">Bridge</SelectItem>
                    <SelectItem value="Commercial">Commercial</SelectItem>
                    <SelectItem value="Hard Money">Hard Money</SelectItem>
                    <SelectItem value="Ground Up Construction">Ground Up Construction</SelectItem>
                    <SelectItem value="Business Loan">Business Loan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="interest_rate">Interest Rate *</Label>
                <Input
                  id="interest_rate"
                  value={formData.interest_rate}
                  onChange={(e) => setFormData({ ...formData, interest_rate: e.target.value })}
                  placeholder="e.g., 8.5% - 9.5%"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="term_months">Term (Months) *</Label>
                <Input
                  id="term_months"
                  type="number"
                  value={formData.term_months}
                  onChange={(e) => setFormData({ ...formData, term_months: e.target.value })}
                  placeholder="e.g., 12"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="min_fico">Minimum FICO Score *</Label>
                <Input
                  id="min_fico"
                  type="number"
                  value={formData.min_fico}
                  onChange={(e) => setFormData({ ...formData, min_fico: e.target.value })}
                  placeholder="e.g., 650"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_ltv">Maximum LTV (%) *</Label>
                <Input
                  id="max_ltv"
                  type="number"
                  value={formData.max_ltv}
                  onChange={(e) => setFormData({ ...formData, max_ltv: e.target.value })}
                  placeholder="e.g., 75"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="min_loan_amount">Min Loan Amount ($) *</Label>
                <Input
                  id="min_loan_amount"
                  type="number"
                  value={formData.min_loan_amount}
                  onChange={(e) => setFormData({ ...formData, min_loan_amount: e.target.value })}
                  placeholder="e.g., 75000"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_loan_amount">Max Loan Amount ($) *</Label>
                <Input
                  id="max_loan_amount"
                  type="number"
                  value={formData.max_loan_amount}
                  onChange={(e) => setFormData({ ...formData, max_loan_amount: e.target.value })}
                  placeholder="e.g., 2500000"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="property_types">Property Types (comma-separated) *</Label>
              <Input
                id="property_types"
                value={formData.property_types}
                onChange={(e) => setFormData({ ...formData, property_types: e.target.value })}
                placeholder="e.g., Residential, Multifamily, Mixed Use"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional program requirements or notes..."
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setShowDialog(false); resetForm(); }}>
                Cancel
              </Button>
              <Button onClick={handleSubmit}>
                {editingProgram ? 'Update Program' : 'Add Program'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductMatrix;
